import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';
import api from '../lib/api';

/**
 * Suburb autocomplete — hits GET /api/geography/suburbs?q=…
 * Debounced 250ms, keyboard navigable (↑ ↓ Enter Esc).
 *
 * Props:
 *  - value: string (current input text)
 *  - onChange: (str) => void (fires on any keystroke)
 *  - onSelect: ({suburb, postcode, state, lat, lng}) => void (fires on pick)
 *  - testid: base data-testid
 */
export default function SuburbAutocomplete({ value, onChange, onSelect, testid = 'suburb-autocomplete' }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!value || value.trim().length < 2) {
      setResults([]); setOpen(false); return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/geography/suburbs?q=${encodeURIComponent(value.trim())}`);
        setResults(res.data.results || []);
        setOpen(true);
        setHighlight(0);
      } catch {
        // Silent — autocomplete is best-effort
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [value]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const pick = (item) => {
    onSelect?.(item);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(results[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <Input
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        onFocus={() => { if (results.length) setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder="e.g. Surry Hills"
        autoComplete="off"
        data-testid={testid}
      />
      {open && (results.length > 0 || loading) && (
        <div
          className="absolute z-50 mt-1 w-full max-h-64 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg"
          data-testid={`${testid}-dropdown`}
        >
          {loading && <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>}
          {results.map((r, i) => (
            <button
              key={`${r.suburb}-${r.postcode}`}
              type="button"
              onClick={() => pick(r)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm ${i === highlight ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50 text-gray-900'}`}
              data-testid={`${testid}-item-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="font-medium">{r.suburb}</span>
              <span className="text-xs text-gray-500 ml-auto font-mono">{r.state} {r.postcode}</span>
            </button>
          ))}
          {!loading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-500">No matches</div>
          )}
        </div>
      )}
    </div>
  );
}
